import { useState, type InputHTMLAttributes } from "react";
import { Eye, EyeOff } from "lucide-react";

export interface PasswordInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: string;
  hint?: string;
  error?: string;
}

/**
 * Password input with show/hide toggle using Lucide Eye/EyeOff icons.
 * Accessible with proper aria labels and keyboard support.
 * Reusable across login, signup, reset password, and any password form.
 */
export function PasswordInput({
  label,
  hint,
  error,
  id,
  className = "",
  ...props
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  const inputId =
    id || (label ? label.toLowerCase().replace(/\s+/g, "-") : "password");

  return (
    <div className={`form-group ${className}`}>
      {label ? (
        <label className="form-label" htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      {hint && !error ? (
        <span className="form-hint">{hint}</span>
      ) : null}
      <div className="password-wrapper">
        <input
          id={inputId}
          type={visible ? "text" : "password"}
          className={`form-input ${error ? "form-input--error" : ""}`}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-error` : undefined}
          {...props}
        />
        <button
          type="button"
          className="password-toggle"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
          tabIndex={-1}
        >
          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
      {error ? (
        <div className="form-error" id={`${inputId}-error`} role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}
